import { Callout } from "@dashboard/components/Callout/Callout";
import { DashboardCard } from "@dashboard/components/Card";
import Dropzone from "@dashboard/components/Dropzone";
import { visuallyHidden } from "@dashboard/utils/visuallyHidden";
import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import {
  type AtticAssetUploadItem,
  type AtticAssetUploadStatus,
  isCancellableUpload,
  isFinishedUpload,
} from "../../hooks/useAtticAssetUploads";
import { uploadMessages } from "../../messages";
import {
  ATTIC_ASSET_ACCEPTED_TYPES,
  type AtticAssetDroppedFile,
  type AtticAssetUploadEntry,
  formatBytes,
  getAtticAssetUploadPath,
  isValidSubpath,
  normaliseSubpath,
  planAtticAssetUploads,
} from "../../utils";

export interface AtticAssetUploadCardProps {
  maxUploadBytes: number;
  /**
   * The directory takes uploads. When it stops (an app claimed it), the drop
   * area goes but the rows stay, so what was in flight still says how it ended.
   */
  writable: boolean;
  items: AtticAssetUploadItem[];
  /** Something is queued or running: leaving the page would cancel it. */
  inFlight: boolean;
  onUpload: (entries: AtticAssetUploadEntry[]) => void;
  /** Stable across renders (the queue's own callback), so unchanged rows skip rendering. */
  onCancel: (id: number) => void;
  onClearFinished: () => void;
}

/** The live region speaks at most this often while uploads run, and at once when they end. */
export const ANNOUNCE_INTERVAL_MS = 5_000;

const statusColor: Record<AtticAssetUploadStatus, "default2" | "success1" | "critical1"> = {
  queued: "default2",
  create: "default2",
  put: "default2",
  confirm: "default2",
  done: "success1",
  failed: "critical1",
  cancelled: "default2",
};

interface UploadRowProps {
  item: AtticAssetUploadItem;
  onCancel: (id: number) => void;
}

/**
 * One row. Memoised: the queue replaces only the item that changed, so a
 * progress tick or a status change renders that row and no other, however
 * large the drop.
 */
const UploadRow = memo(({ item, onCancel }: UploadRowProps) => {
  const intl = useIntl();
  const path = getAtticAssetUploadPath(item);

  return (
    <Box
      as="li"
      display="grid"
      __gridTemplateColumns="minmax(0, 1fr) auto auto auto"
      gap={4}
      alignItems="baseline"
      data-test-id="attic-asset-upload-row"
    >
      <Box display="flex" flexDirection="column">
        <Text size={3} ellipsis title={item.filename}>
          {path}
        </Text>
        {item.error && (
          <Text size={2} color="critical1" data-test-id="attic-asset-upload-error">
            {item.error}
          </Text>
        )}
      </Box>
      <Text size={2} color="default2">
        {formatBytes(item.size, intl.locale)}
      </Text>
      <Text size={2} color={statusColor[item.status]} fontWeight="medium">
        {item.status === "put" && item.progress !== undefined
          ? intl.formatMessage(uploadMessages.progress, { percent: item.progress })
          : intl.formatMessage(uploadMessages[item.status])}
      </Text>
      <Box __minWidth="64px" display="flex" justifyContent="flex-end">
        {isCancellableUpload(item.status) && (
          <Button
            variant="tertiary"
            size="small"
            onClick={() => onCancel(item.id)}
            aria-label={intl.formatMessage(uploadMessages.cancelLabel, { path })}
            data-test-id="attic-asset-upload-cancel"
          >
            {intl.formatMessage(uploadMessages.cancel)}
          </Button>
        )}
      </Box>
    </Box>
  );
});

UploadRow.displayName = "AtticAssetUploadRow";

/**
 * What the live region says: the latest counts, but changed at most once per
 * ANNOUNCE_INTERVAL_MS while uploads run (a 500-file drop would otherwise
 * speak once per file), and at once when nothing is running any more.
 */
const useThrottledAnnouncement = (text: string, settled: boolean) => {
  const [announced, setAnnounced] = useState(text);
  // What the region holds when it mounts counts as said now.
  const lastAt = useRef(text ? Date.now() : Number.NEGATIVE_INFINITY);

  useEffect(() => {
    if (text === announced) {
      return;
    }

    const wait = settled ? 0 : Math.max(0, lastAt.current + ANNOUNCE_INTERVAL_MS - Date.now());
    const timer = setTimeout(() => {
      lastAt.current = Date.now();
      setAnnounced(text);
    }, wait);

    return () => clearTimeout(timer);
  }, [text, settled, announced]);

  return announced;
};

/**
 * Drop files (or a folder) into the directory. Every file is checked in the
 * browser first (type, size, folder names), then goes create -> PUT ->
 * confirm; the row says which, with the share sent while uploading. A
 * dropped folder's names are cleaned into ones the backend takes, and each
 * row shows the path the file will have.
 */
export const AtticAssetUploadCard = ({
  maxUploadBytes,
  writable,
  items,
  inFlight,
  onUpload,
  onCancel,
  onClearFinished,
}: AtticAssetUploadCardProps) => {
  const intl = useIntl();
  const [subpath, setSubpath] = useState("");
  // What the last drop left out (dotfiles, OS litter), so it never does nothing silently.
  const [dropNotice, setDropNotice] = useState<string | null>(null);
  const subpathInvalid = !isValidSubpath(normaliseSubpath(subpath));
  const hasFinished = items.some(item => isFinishedUpload(item.status));
  const counts = useMemo(
    () =>
      items.reduce(
        (acc, item) => {
          if (item.status === "done") {
            acc.done += 1;
          } else if (item.status === "failed") {
            acc.failed += 1;
          } else if (item.status === "cancelled") {
            acc.cancelled += 1;
          } else {
            acc.active += 1;
          }

          return acc;
        },
        { active: 0, done: 0, failed: 0, cancelled: 0 },
      ),
    [items],
  );
  const announcement = useThrottledAnnouncement(
    items.length ? intl.formatMessage(uploadMessages.statusSummary, counts) : "",
    counts.active === 0,
  );
  const drop = (files: AtticAssetDroppedFile[]) => {
    const plan = planAtticAssetUploads(files, subpath);

    if (!plan.entries.length) {
      setDropNotice(intl.formatMessage(uploadMessages.nothingToUpload, { ignored: plan.ignored }));

      return;
    }

    setDropNotice(
      plan.ignored
        ? intl.formatMessage(uploadMessages.ignoredSkipped, { ignored: plan.ignored })
        : null,
    );
    onUpload(plan.entries);
  };

  if (!writable && !items.length) {
    return null;
  }

  return (
    <DashboardCard data-test-id="attic-asset-upload">
      <DashboardCard.Header>
        <DashboardCard.Title>{intl.formatMessage(uploadMessages.title)}</DashboardCard.Title>
        {hasFinished && (
          <DashboardCard.Toolbar>
            <Button
              variant="secondary"
              onClick={onClearFinished}
              data-test-id="attic-asset-upload-clear"
            >
              {intl.formatMessage(uploadMessages.clearFinished)}
            </Button>
          </DashboardCard.Toolbar>
        )}
      </DashboardCard.Header>
      <DashboardCard.Content display="flex" flexDirection="column" gap={4}>
        {writable && (
          <Input
            label={intl.formatMessage(uploadMessages.subpath)}
            value={subpath}
            onChange={event => setSubpath(event.target.value)}
            error={subpathInvalid}
            helperText={intl.formatMessage(
              subpathInvalid ? uploadMessages.refusedFolder : uploadMessages.subpathHelp,
            )}
            data-test-id="attic-asset-upload-subpath"
          />
        )}
        {/* The picker offers only the accepted types; a drop of anything else is
            refused on its own row, with the reason, rather than ignored. A
            dropped folder keeps its subfolders; dotfiles are skipped. */}
        {writable && (
          <Dropzone onDrop={drop}>
            {({
              isDragActive,
              getInputProps,
              getRootProps,
            }: {
              isDragActive: boolean;
              getInputProps: () => object;
              getRootProps: () => object;
            }) => (
              <Box
                {...getRootProps()}
                __borderStyle="dashed"
                borderWidth={1}
                borderColor={isDragActive ? "accent1" : "default1"}
                backgroundColor={isDragActive ? "accent1Hovered" : "default1"}
                borderRadius={3}
                padding={6}
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                cursor="pointer"
                data-test-id="attic-asset-upload-dropzone"
              >
                <input {...getInputProps()} accept={ATTIC_ASSET_ACCEPTED_TYPES.join(",")} />
                <Text size={4} fontWeight="medium">
                  {intl.formatMessage(uploadMessages.dropHint)}
                </Text>
                <Text size={2} color="default2" textAlign="center">
                  {intl.formatMessage(uploadMessages.limits, {
                    max: formatBytes(maxUploadBytes, intl.locale),
                  })}
                </Text>
              </Box>
            )}
          </Dropzone>
        )}
        {inFlight && (
          // The Dashboard's exit dialog only says "Leave without saving
          // changes?" and cannot be reworded per form, so this says it here.
          <Box data-test-id="attic-asset-upload-leave-notice">
            <Callout type="warning" title={intl.formatMessage(uploadMessages.leaveNotice)}>
              {null}
            </Callout>
          </Box>
        )}
        {dropNotice && (
          <Text size={2} color="default2" data-test-id="attic-asset-upload-notice">
            {dropNotice}
          </Text>
        )}
        <div role="status" aria-live="polite" style={visuallyHidden}>
          {announcement}
        </div>
        {items.length > 0 && (
          <Box
            as="ul"
            display="flex"
            flexDirection="column"
            gap={2}
            data-test-id="attic-asset-uploads"
          >
            {items.map(item => (
              <UploadRow key={item.id} item={item} onCancel={onCancel} />
            ))}
          </Box>
        )}
      </DashboardCard.Content>
    </DashboardCard>
  );
};

export default AtticAssetUploadCard;
